/**
 * <cart-product-recommendations>
 *   Lazy-loads Shopify product recommendations into the cart drawer.
 *   If server-side rendering already populated the element with a heading
 *   + product list, the JS skips the fetch.
 *   Otherwise, fetches /recommendations/products?product_id=X&intent=Y&limit=N
 *   and clones the matching container's children into this element.
 *
 * <add-to-cart-mini>
 *   Lightweight wrapper around the FBT "ADD" button form. Catches submit,
 *   POSTs to /cart/add.js with sections=header-mini-cart-content,...
 *   so MiniCart.renderContents updates the drawer in place. Drawer stays
 *   open after the add — no full-page navigation.
 */

if (!customElements.get("cart-product-recommendations")) {
  customElements.define(
    "cart-product-recommendations",
    class CartProductRecommendations extends HTMLElement {
      connectedCallback() {
        this.productId = this.dataset.productId;
        this.target = this.dataset.target || "cart-drawer__rec-list";
        this.limit = this.dataset.limit || "2";
        this.intent = this.dataset.intent || "related";
        this.sectionId = this.dataset.sectionId || "cart-drawer-recommendations";

        // If server-side rendering already produced content, no need to fetch.
        const hasContent = this.querySelector("." + this.target);
        if (hasContent) return;
        if (!this.productId) return;

        this.fetchRecommendations();
      }

      async fetchRecommendations() {
        const url = `${window.Shopify.routes.root}recommendations/products?product_id=${this.productId}&limit=${this.limit}&intent=${this.intent}&section_id=${this.sectionId}`;

        try {
          const response = await fetch(url);
          if (!response.ok) return;
          const text = await response.text();
          // Source is Shopify's own server-rendered HTML for our section;
          // we extract via DOMParser and clone nodes (no innerHTML).
          const parsed = new DOMParser().parseFromString(text, "text/html");
          const wrapper = parsed.querySelector("cart-product-recommendations");
          const container = wrapper && wrapper.querySelector("." + this.target);

          if (container && container.children.length > 0) {
            // Move parsed children into this element (replaces existing content)
            this.replaceChildren(...Array.from(wrapper.childNodes));
          } else {
            // No recommendations available — hide the section
            this.style.display = "none";
          }
        } catch (e) {
          // Silently fail — recommendations are non-critical
          this.style.display = "none";
        }
      }
    }
  );
}

if (!customElements.get("add-to-cart-mini")) {
  customElements.define(
    "add-to-cart-mini",
    class AddToCartMini extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector("form");
        if (!this.form) return;
        this.button = this.form.querySelector('button[type="submit"]');
        this.form.addEventListener("submit", this.handleSubmit.bind(this));
      }

      async handleSubmit(event) {
        event.preventDefault();
        if (!this.button || this.button.classList.contains("loading")) return;

        this.button.classList.add("loading");
        this.button.setAttribute("aria-disabled", "true");

        const formData = new FormData(this.form);
        formData.append("sections", "header-mini-cart-content,header-mini-cart-footer,header");
        formData.append("sections_url", window.location.pathname);

        try {
          const response = await fetch(window.routes.cart_add_url, {
            method: "POST",
            headers: {
              "X-Requested-With": "XMLHttpRequest",
              Accept: "application/javascript",
            },
            body: formData,
          });

          const data = await response.json();

          if (data.status) {
            console.error("Add to cart error:", data.description || data.message);
            return;
          }

          // Trigger the existing MiniCart renderContents flow on the visible drawer
          const miniCart =
            Array.from(document.querySelectorAll("mini-cart")).find(
              (mc) => mc.offsetParent !== null,
            ) || document.querySelector("mini-cart");

          if (miniCart && typeof miniCart.renderContents === "function") {
            miniCart.renderContents(data);
          }

          document.dispatchEvent(
            new CustomEvent("cart:item-add", {
              detail: { items: [data], context: "FBT" },
            }),
          );
        } catch (e) {
          console.error("Add to cart fetch failed:", e);
        } finally {
          this.button.classList.remove("loading");
          this.button.removeAttribute("aria-disabled");
        }
      }
    }
  );
}
